import { TabItem, Tabs } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, matchPath, useNavigate } from 'react-router-dom';
import PageHeader from 'src/js/component/page-header';
import useLocalStorageWithState from 'src/js/component/useLocalStorageWithState';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { compileWithArgs } from 'src/js/helper/path-to-regexp-helper';
import { useNavigateListener } from 'src/js/hook/use-history-listener';
import { useStores } from 'src/js/hook/use-stores';
import { StreamKey, StreamPageParameters } from 'src/js/pages/streaming/streaming-router';
import { MakitoPaths } from 'src/js/route';
import SidebarLayout from 'src/js/sidebar';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/constant';

export const Streaming: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionStore } = useStores();
  const [selectedTab, setSelectedTab] = useLocalStorageWithState(
    `${constant.lStorage.streamingTab}:${sessionStore.username}`,
    StreamKey.STREAMS,
  );

  const [tabs, setTabs] = useState<string[]>([]);
  const updateTabs = () => {
    let tabs = Object.values(StreamKey);

    // Check if 2110 is licensed
    if (!systemInfoCapabilities.is2110()) {
      tabs = tabs.filter((v) => v !== StreamKey.INPUTS);
    }

    // Check if metadata is licensed
    if (!systemInfoCapabilities.hasMetadata()) {
      tabs = tabs.filter((v) => v !== StreamKey.METADATA);
    }

    // Check if scte is licensed
    if (!systemInfo.scte || systemInfo.hasDisplayPort) {
      tabs = tabs.filter((v) => v !== StreamKey.ADINSERTIONS);
    }

    setTabs(tabs);
  };

  useEffect(() => {
    updateTabs();
  }, []);
  useWatchObject(systemInfo, updateTabs);

  useNavigateListener((newLocation) => {
    const routeName = '/streaming/:page';
    const matchedParam = matchPath(routeName, newLocation.pathname);
    // NKE-2600 Prevent people from directly accessing tabs that are currently unavailable (or from redirect after login)
    if (!isNil(matchedParam?.params) && !isNil(tabs.find((t) => t === matchedParam.params?.page))) {
      setSelectedTab(matchedParam.params?.page as StreamKey);
    }
  });

  return (
    <SidebarLayout>
      <PageHeader title={t('sidebar.streams')} iconName="Streams" />
      <Tabs
        selectedTab={selectedTab}
        onSelect={(event: ChangeEvent<{ eventkey: StreamKey }>) => {
          setSelectedTab(event.target.eventkey);
          navigate(
            compileWithArgs<StreamPageParameters>(MakitoPaths.streaming)({
              page: event.target.eventkey,
            }),
            { replace: true },
          );
        }}
      >
        {tabs.map(
          (tab): ReactNode => (
            <TabItem key={tab} caption={t(`streaming.tabs.${tab}`)} eventkey={tab} />
          ),
        )}
      </Tabs>
      <Outlet />
    </SidebarLayout>
  );
};
